import { BrowserModule } from '@angular/platform-browser'
import { NgModule } from '@angular/core'
import { AppRoutingModule } from './app-routing.module'
import { APP_BASE_HREF, DatePipe } from '@angular/common'
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http'
import { TranslateModule, TranslateLoader } from '@ngx-translate/core'
import { TranslateHttpLoader } from '@ngx-translate/http-loader'
import { NgxLoadingModule } from 'ngx-loading'
import { BsDropdownModule } from 'ngx-bootstrap/dropdown'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE, DateAdapter } from '@angular/material'
import { MatDividerModule, MatCardModule, MatButtonModule, MatSelectModule, MatChipsModule } from '@angular/material'
import { MatDatepickerModule, MatNativeDateModule, MatDialogModule } from '@angular/material'
import { MatTableModule } from '@angular/material/table'
import { MatInputModule } from '@angular/material/input'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatPaginatorModule } from '@angular/material/paginator'
import { MatExpansionModule } from '@angular/material/expansion'
import { MomentDateAdapter } from '@angular/material-moment-adapter'
import { NgxSliderModule } from '@angular-slider/ngx-slider'
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material'
import { MatAutocompleteModule } from '@angular/material/autocomplete'

// Guards
import { AuthGuard } from './guards/auth.guard'
import { LeavePageGuard } from './guards/leave-page/leave-page.guard'

// Helpers
import { JwtInterceptor } from './helpers/jwt.interceptor'
import { ImageCropperModule } from 'ngx-image-cropper'

// Components
import { AppComponent } from './app.component'
import { HeaderComponent } from './components/header/header.component'
import { SidebarComponent } from './components/sidebar/sidebar.component'
import { ModalComponent } from './components/modal/modal.component'
import { AlertComponent } from './components/alert/alert.component'
import { LocationPickerComponent } from './components/location-picker/location-picker.component'

// Pages
import { LoginComponent } from './pages/login/login.component'
import { SignupComponent } from './pages/signup/signup.component'
import { VirtualEchargerComponent } from './pages/echargers/virtual-echarger/virtual-echarger.component'

// Services
import { LoadingService } from './services/loading.service'
import { LoadingComponent } from './components/loading/loading.component'
import { CheckboxContainerComponent } from './components/checkbox-container/checkbox-container.component'
import { FlexLayoutModule } from '@angular/flex-layout'
import { NgxChartsModule } from '@swimlane/ngx-charts'
import { BackNavigationService } from './services/back-navigation.service'
import { RouteReuseStrategy } from '@angular/router'
import { PathBasedReuseStrategy } from './path-based-reuse-strategy'
import { ExcelService } from './services/excel.service'
import { SearchSiteComponent } from './pages/sites/search-site/search-site.component'
import { CreateSiteComponent } from './pages/sites/create-site/create-site.component'
import { CreateZoneComponent } from './pages/sites/create-site/create-zone/create-zone.component'

export function HttpLoaderFactory (http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json')
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    SidebarComponent,
    SignupComponent,
    HeaderComponent,
    VirtualEchargerComponent,
    CreateZoneComponent,
    CreateSiteComponent,
    SearchSiteComponent,
    ModalComponent,
    AlertComponent,
    LoadingComponent,
    CheckboxContainerComponent,
    LocationPickerComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    NgxLoadingModule.forRoot({}),
    BsDropdownModule.forRoot(),
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    MatAutocompleteModule,
    MatTableModule,
    MatInputModule,
    MatCheckboxModule,
    MatPaginatorModule,
    MatExpansionModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCardModule,
    MatSelectModule,
    ImageCropperModule,
    MatDialogModule,
    NgxSliderModule,
    NgxDaterangepickerMd.forRoot(),
    FlexLayoutModule,
    MatDividerModule,
    NgxChartsModule,
    MatButtonModule,
    MatSelectModule,
    MatChipsModule
  ],
  providers: [
    AuthGuard,
    ExcelService,
    { provide: APP_BASE_HREF, useValue: '/' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: JwtInterceptor,
      multi: true
    },
    LoadingService,
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE]
    },
    {
      provide: MAT_DATE_FORMATS, useValue: {
        parse: { dateInput: 'LL' },
        display: {
          dateInput: 'DD MMM YYYY',
          monthYearLabel: 'YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'YYYY'
        }
      }
    },
    LeavePageGuard,
    DatePipe,
    BackNavigationService,
    { provide: RouteReuseStrategy, useClass: PathBasedReuseStrategy }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
